import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AUTOMATIC_SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { VehicleClass } from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { useQuantityHelpers } from './useQuantityHelpers.ts';
import { useSharedFormQuantities } from './useSharedFormQuantities.ts';
import { deriveQuantity } from '../utils/deriveQuantity.ts';

export function useOrderFormQuantities(methods: UseFormReturn<AutomaticProductsFormValues>) {
    const { getValues, setValue } = methods;
    const { recalculatePurchaseQuantities, recalculateAll } = useQuantityHelpers(methods);
    const shared = useSharedFormQuantities(methods);

    const recalculateFromTransportNumber = useCallback(
        (transportNumber: number, vehicleClass: VehicleClass) => {
            const [productType, salesMaterialUnit, salesTransportUnit] = getValues([
                'productType',
                'salesMaterialUnit',
                'salesTransportUnit',
            ]);
            const { payloadInTons } = vehicleClass;

            if (isTransportProduct(productType)) {
                if (salesTransportUnit) {
                    const qty = deriveQuantity(transportNumber, 'TRANSPORT', salesTransportUnit, payloadInTons);
                    setValue('salesTransportQuantity', qty);
                    recalculatePurchaseQuantities(qty, salesTransportUnit, payloadInTons);
                }
            } else {
                if (salesMaterialUnit) {
                    const qty = deriveQuantity(transportNumber, 'TRANSPORT', salesMaterialUnit, payloadInTons);
                    setValue('salesMaterialQuantity', qty);
                    recalculateAll(qty, salesMaterialUnit, payloadInTons);
                }
            }
        },
        [getValues, setValue, recalculatePurchaseQuantities, recalculateAll]
    );

    const onVehicleClassChange = useCallback(
        (vehicleClass?: VehicleClass) => {
            if (!vehicleClass) {
                return;
            }
            const transportNumber = getValues('transportNumber');

            if (transportNumber !== undefined) {
                recalculateFromTransportNumber(transportNumber, vehicleClass);
            }
        },
        [getValues, recalculateFromTransportNumber]
    );

    const onTransportNumberChange = useCallback(
        (transportNumber?: number) => {
            if (transportNumber === undefined) {
                return;
            }
            const vehicleClass = getValues('vehicleClass');

            if (vehicleClass) {
                recalculateFromTransportNumber(transportNumber, vehicleClass);
            }
        },
        [getValues, recalculateFromTransportNumber]
    );

    const onSalesMaterialUnitChange = useCallback(
        (salesMaterialUnit?: AUTOMATIC_SUPPORTED_UNITS) => {
            if (!salesMaterialUnit) {
                return;
            }
            const [productType, transportNumber, vehicleClass] = getValues([
                'productType',
                'transportNumber',
                'vehicleClass',
            ]);
            if (vehicleClass && !isTransportProduct(productType) && transportNumber !== undefined) {
                const qty = deriveQuantity(transportNumber, 'TRANSPORT', salesMaterialUnit, vehicleClass.payloadInTons);
                setValue('salesMaterialQuantity', qty);
                recalculateAll(qty, salesMaterialUnit, vehicleClass.payloadInTons);
            }
        },
        [getValues, setValue, recalculateAll]
    );

    const onSalesTransportUnitChange = useCallback(
        (unit?: AUTOMATIC_SUPPORTED_UNITS) => {
            if (!unit) {
                return;
            }
            const [productType, transportNumber, vehicleClass] = getValues([
                'productType',
                'transportNumber',
                'vehicleClass',
            ]);
            if (!vehicleClass) {
                return;
            }

            if (isTransportProduct(productType)) {
                if (transportNumber !== undefined) {
                    const qty = deriveQuantity(transportNumber, 'TRANSPORT', unit, vehicleClass.payloadInTons);
                    setValue('salesTransportQuantity', qty);
                    recalculatePurchaseQuantities(qty, unit, vehicleClass.payloadInTons);
                }
            } else {
                if (transportNumber !== undefined) {
                    setValue(
                        'salesTransportQuantity',
                        deriveQuantity(transportNumber, 'TRANSPORT', unit, vehicleClass.payloadInTons)
                    );
                }
            }
        },
        [getValues, setValue, recalculatePurchaseQuantities]
    );

    return {
        ...shared,
        onSalesMaterialUnitChange,
        onVehicleClassChange,
        onTransportNumberChange,
        onSalesTransportUnitChange,
    };
}
